import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CheckCircle2,
  Wind,
  Layers,
  Activity,
  Camera,
  Zap,
  Triangle,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { captureRefFromUrl } from '../lib/growth';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Brand palette (mirrors Playbook.js / coach-lewis-frameworks.pdf)
const TEAL = '#004C54';
const TEAL_DEEP = '#003a40';
const CYAN = '#4FBCC7';
const CREAM = '#F5F3EE';

function OptInForm({ id, source, onDone, dataTestPrefix }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/checklist/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          source: source || 'checklist_landing',
        }),
      });
      if (r.ok) {
        const body = await r.json();
        onDone(body);
      } else {
        const b = await r.json().catch(() => ({}));
        setErr(b.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      id={id}
      onSubmit={submit}
      className="space-y-3"
      data-testid={`${dataTestPrefix}-form`}
    >
      <Input
        placeholder="First name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
        className="bg-white h-12"
        data-testid={`${dataTestPrefix}-firstname`}
      />
      <Input
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="bg-white h-12"
        data-testid={`${dataTestPrefix}-email`}
      />
      {err && (
        <p
          className="text-[13px] text-red-700"
          data-testid={`${dataTestPrefix}-error`}
        >
          {err}
        </p>
      )}
      <Button
        type="submit"
        disabled={loading || !firstName || !email}
        className="w-full font-bold h-12 text-[14px]"
        style={{ background: TEAL, color: '#fff' }}
        data-testid={`${dataTestPrefix}-submit`}
      >
        {loading ? 'Sending…' : 'Send Me the Free Checklist →'}
      </Button>
      <p
        className="text-[11.5px] text-center"
        style={{ color: '#666' }}
        data-testid={`${dataTestPrefix}-fineprint`}
      >
        No spam. Unsubscribe anytime. We respect your inbox.
      </p>
    </form>
  );
}

const PROTOCOLS = [
  { icon: Wind,     n: '01', name: 'The 3-Breath Reset',         when: 'Pre-game | In-game' },
  { icon: Layers,   n: '02', name: 'The ABC Confidence Stack',       when: 'Pre-game' },
  { icon: Activity, n: '03', name: 'Green-Yellow-Red Awareness', when: 'Pre-game | In-game' },
  { icon: Camera,   n: '04', name: 'The Camera Drill',           when: 'In-game | Training' },
  { icon: Zap,      n: '05', name: 'Bounce-Back 3R',             when: 'In-game | Post-game' },
  { icon: Triangle, n: '06', name: 'The Performance Pyramid',    when: 'Post-game' },
];

export default function Checklist() {
  const navigate = useNavigate();
  const [src] = useState(() => {
    // Capture campaign source from URL (utm_source / ?ref) for attribution.
    try {
      captureRefFromUrl();
    } catch {
      // referral capture is best-effort
    }
    if (typeof window === 'undefined') return 'checklist_landing';
    const u = new URL(window.location.href);
    const utm = u.searchParams.get('utm_source');
    if (utm) return `checklist_${utm.toLowerCase().replace(/[^a-z0-9_]/g, '_')}`;
    return 'checklist_landing';
  });

  const handleDone = (body) => {
    const pdfUrl = body?.pdf_url || '/mental-performance-checklist.pdf';
    navigate(`/checklist/thank-you?pdf=${encodeURIComponent(pdfUrl)}`);
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: CREAM }}
      data-testid="checklist-page"
    >
      {/* Top utility bar */}
      <div
        style={{ background: TEAL, color: 'rgba(255,255,255,0.85)' }}
        className="text-[12px]"
      >
        <div className="max-w-[1120px] mx-auto px-5 py-2 flex items-center justify-between">
          <Link
            to="/"
            className="font-mono-accent uppercase tracking-[0.18em] hover:text-white"
            data-testid="checklist-back-home"
          >
            ← apexathleticmind.com
          </Link>
          <a
            href="#second-cta"
            className="hidden sm:inline font-semibold hover:text-white"
            style={{ color: CYAN }}
            data-testid="checklist-top-cta-link"
          >
            Get the checklist →
          </a>
        </div>
      </div>

      {/* ===== HERO ===== */}
      <section style={{ background: CREAM }}>
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-14 sm:py-20 grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-14 items-center">
          {/* Left: copy + form */}
          <div>
            <div
              className="font-mono-accent uppercase tracking-[0.26em] text-[10.5px] font-bold mb-5"
              style={{ color: CYAN }}
              data-testid="checklist-eyebrow"
            >
              Free for athletes
            </div>
            <h1
              className="font-heading text-[2rem] sm:text-[2.8rem] md:text-[3.2rem] font-bold tracking-tight leading-[1.05] mb-5"
              style={{ color: TEAL }}
              data-testid="checklist-hero-title"
            >
              Train your mind <span style={{ color: CYAN }}>like a pro.</span>
            </h1>
            <p
              className="text-[16px] sm:text-[18px] leading-relaxed text-[#1a1a1a] mb-6 max-w-[560px]"
              data-testid="checklist-hero-sub"
            >
              The same 6 mental performance protocols Coach Lewis uses with serious &amp; elite athletes. Print, fill it in, take it to the field.
            </p>
            <OptInForm
              id="checklist-form-1"
              source={src}
              onDone={handleDone}
              dataTestPrefix="checklist-hero"
            />
          </div>

          {/* Right: PDF cover preview */}
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_18px_50px_rgba(0,76,84,0.25)] mx-auto w-full max-w-[360px] aspect-[3/4]"
            style={{ background: TEAL }}
            data-testid="checklist-cover-preview"
          >
            {/* Diagonal accent (matches PDF cover) */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  `linear-gradient(225deg, ${CYAN} 0%, ${CYAN} 18%, transparent 18.5%)`,
              }}
            />
            {/* Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
              <div
                className="font-mono-accent uppercase tracking-[0.22em] text-[8.5px] font-bold mb-6"
                style={{ color: CYAN }}
              >
                A Coach Lewis Playbook
              </div>
              <h3
                className="font-heading font-bold leading-[1.05] mb-3 text-[32px] text-white"
              >
                Mental Performance
                <br />
                Checklist
              </h3>
              <div
                className="w-16 h-[2px] my-4"
                style={{ background: CYAN }}
              />
              <p
                className="text-[12.5px] leading-relaxed text-white/80"
              >
                Compete with Confidence.
                <br />
                Perform Under Pressure.
              </p>
              <p
                className="text-[10.5px] mt-6 font-mono-accent uppercase tracking-[0.18em]"
                style={{ color: CYAN }}
              >
                6 Protocols · 10 Pages
              </p>
            </div>
            {/* Bottom band */}
            <div
              className="absolute inset-x-0 bottom-0 h-12 flex items-center justify-center"
              style={{ background: TEAL_DEEP }}
            >
              <p
                className="text-[10px] uppercase tracking-[0.18em] font-bold"
                style={{ color: CYAN }}
              >
                apexathleticmind.com
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHAT'S INSIDE ===== */}
      <section
        className="bg-white border-y"
        style={{ borderColor: 'rgba(0,76,84,0.1)' }}
      >
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div className="text-center mb-10">
            <div
              className="font-mono-accent uppercase tracking-[0.24em] text-[10px] font-bold mb-3"
              style={{ color: CYAN }}
            >
              The Apex Six — What’s inside
            </div>
            <h2
              className="font-heading text-[1.9rem] sm:text-[2.3rem] font-bold tracking-tight leading-tight"
              style={{ color: TEAL }}
              data-testid="checklist-inside-title"
            >
              6 protocols. One per page. Ready to run today.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 max-w-[920px] mx-auto">
            {PROTOCOLS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.n}
                  className="rounded-xl p-5 border"
                  style={{
                    background: CREAM,
                    borderColor: 'rgba(0,76,84,0.1)',
                  }}
                  data-testid={`checklist-inside-${p.n}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ background: TEAL, color: CYAN }}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div
                      className="font-mono-accent uppercase tracking-[0.18em] text-[10px] font-bold"
                      style={{ color: TEAL }}
                    >
                      Protocol {p.n}
                    </div>
                  </div>
                  <h3
                    className="font-heading text-[16px] font-bold mb-1"
                    style={{ color: TEAL }}
                  >
                    {p.name}
                  </h3>
                  <p className="text-[12.5px] text-[#3a3a3a] leading-relaxed">
                    {p.when}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== WHY IT MATTERS ===== */}
      <section style={{ background: CREAM }}>
        <div className="max-w-[820px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div
            className="rounded-2xl p-7 sm:p-10 border"
            style={{ background: 'white', borderColor: 'rgba(0,76,84,0.1)' }}
          >
            <div
              className="font-heading text-[44px] leading-none mb-2"
              style={{ color: CYAN }}
            >
              “
            </div>
            <p
              className="font-heading text-[20px] sm:text-[24px] font-bold leading-snug mb-4"
              style={{ color: TEAL }}
              data-testid="checklist-pullquote"
            >
              Talent gets you into the gym. Mental performance is what wins games. The athletes who train their mind on a schedule — not just when they’re struggling — separate themselves.
            </p>
            <p
              className="text-[13px] font-mono-accent uppercase tracking-[0.18em] font-bold"
              style={{ color: CYAN }}
            >
              — Coach Varsay Lewis, M.Ed.
            </p>
          </div>

          <div
            className="mt-8 text-[15.5px] leading-relaxed text-[#1a1a1a] space-y-4"
            data-testid="checklist-explanation"
          >
            <p>
              Most athletes train their bodies five days a week and their mind for 30 seconds before competition. Then they wonder why pressure feels impossible.
            </p>
            <p>
              This checklist is the same one Coach Lewis runs with serious &amp; elite athletes. Print it. Fill it in. Take it to the field. Six protocols you can <strong>run before, during, and after every competition</strong> — starting today.
            </p>
            <p style={{ color: TEAL }} className="font-semibold">
              No theory. No fluff. Just the reps.
            </p>
          </div>
        </div>
      </section>

      {/* ===== TRUST STRIP ===== */}
      <section className="bg-white border-y" style={{ borderColor: 'rgba(0,76,84,0.1)' }}>
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-10">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-center">
            <div className="flex items-center gap-2" data-testid="checklist-trust-credential">
              <ShieldCheck className="h-4 w-4" style={{ color: TEAL }} />
              <span className="text-[12.5px] font-semibold" style={{ color: TEAL }}>
                Coach Varsay Lewis, M.Ed.
              </span>
            </div>
            <div className="flex items-center gap-2" data-testid="checklist-trust-track-record">
              <CheckCircle2 className="h-4 w-4" style={{ color: TEAL }} />
              <span className="text-[12.5px] font-semibold" style={{ color: TEAL }}>
                2025 State Champion Coach
              </span>
            </div>
            <div className="flex items-center gap-2" data-testid="checklist-trust-method">
              <Sparkles className="h-4 w-4" style={{ color: TEAL }} />
              <span className="text-[12.5px] font-semibold" style={{ color: TEAL }}>
                The Apex Six Method
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECOND OPT-IN CTA ===== */}
      <section
        id="second-cta"
        style={{ background: TEAL }}
        className="text-white"
      >
        <div className="max-w-[640px] mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
          <div
            className="font-mono-accent uppercase tracking-[0.24em] text-[10px] font-bold mb-3"
            style={{ color: CYAN }}
          >
            Train the mind like a pro
          </div>
          <h2
            className="font-heading text-[1.9rem] sm:text-[2.4rem] font-bold tracking-tight leading-tight mb-5"
            data-testid="checklist-cta2-title"
          >
            Get the checklist. Take it to the field.
          </h2>
          <p className="text-[14.5px] text-white/75 leading-relaxed mb-7">
            Plus — you’ll get a short email series with the mental performance tools I use with state champions. Unsubscribe anytime.
          </p>
          <div className="max-w-[420px] mx-auto">
            <OptInForm
              id="checklist-form-2"
              source={src}
              onDone={handleDone}
              dataTestPrefix="checklist-cta2"
            />
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer
        style={{ background: TEAL_DEEP }}
        className="text-white/55 text-[12px]"
      >
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-2">
          <Link
            to="/"
            className="hover:text-white"
            data-testid="checklist-footer-home"
          >
            © Apex Athletic Mind
          </Link>
          <div className="flex gap-5 flex-wrap justify-center">
            <Link
              to="/quiz"
              className="hover:text-white"
              data-testid="checklist-footer-quiz"
            >
              Mindset Quiz
            </Link>
            <Link
              to="/assessment"
              className="hover:text-white"
              data-testid="checklist-footer-assessment"
            >
              Apex Six IDP
            </Link>
            <a
              href="mailto:volewis@apexathleticmind.com"
              className="hover:text-white"
              data-testid="checklist-footer-contact"
            >
              Contact
            </a>
            <Link
              to="/unsubscribe"
              className="hover:text-white"
              data-testid="checklist-footer-unsubscribe"
            >
              Unsubscribe
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { Checklist };
